import React, { useState, useEffect, useCallback } from "react";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { trackSearch } from "../../utils/analytics";

/**
 * SearchBar Component
 *
 * A reusable search bar with debouncing, real-time filtering, and keyboard shortcuts.
 * Integrates with the API filtering system for smooth user experience.
 */
export default function SearchBar({
  onSearch,
  placeholder = "Search...",
  debounceMs = 300,
  className = "",
  disabled = false,
  initialValue = "",
  showClearButton = true,
  autoFocus = false,
  ...props
}) {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);

        // Track search event (only if there's a search term)
        if (searchTerm.trim()) {
          trackSearch(searchTerm.trim());
        }
      }
      setIsSearching(false);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, onSearch, debounceMs]);

  // Trigger debounced search when search term changes
  useEffect(() => {
    setIsSearching(searchTerm.length > 0);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [debouncedSearch]);

  // Handle input change
  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle clear search
  const handleClear = () => {
    setSearchTerm("");
    if (onSearch) {
      onSearch("");
    }
    setIsSearching(false);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      handleClear();
      event.target.blur();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon
            className={`h-5 w-5 transition-colors duration-200 ${
              isSearching ? "text-blue-500" : "text-gray-400"
            }`}
          />
        </div>

        {/* Search Input */}
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`
            w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            placeholder-gray-400 text-gray-900
            transition-all duration-200
            ${searchTerm ? "bg-blue-50" : "bg-white"}
          `}
          {...props}
        />

        {/* Clear Button */}
        {showClearButton && searchTerm && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={`
              absolute inset-y-0 right-0 pr-3 flex items-center
              text-gray-400 hover:text-gray-600 
              transition-colors duration-200
              focus:outline-none focus:text-gray-600
            `}
            aria-label="Clear search"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}

        {/* Loading indicator */}
        {isSearching && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>
    </div>
  );
}
